import { useEffect, useRef } from 'react'
import type { Exchange } from '@/stores/sessionStore'
import { ExchangeItem } from './ExchangeItem'

export interface ThreadProps {
  exchanges: Exchange[]
  autoScroll: boolean
  onRerun: (query: string) => void
}

/**
 * Renders an in-progress conversation. `ChatView` only mounts this once
 * `exchanges` is non-empty — the empty-thread landing screen is `ChatHero`.
 */
export function Thread({ exchanges, autoScroll, onRerun }: ThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const stickToBottomRef = useRef(true)

  // Only auto-scroll while the user is already near the bottom. Yanking the
  // viewport away from a table someone is reading is worse than not following.
  useEffect(() => {
    const element = scrollRef.current
    if (!element) return

    const onScroll = () => {
      const distance =
        element.scrollHeight - element.scrollTop - element.clientHeight
      stickToBottomRef.current = distance < 120
    }

    element.addEventListener('scroll', onScroll, { passive: true })
    return () => element.removeEventListener('scroll', onScroll)
  }, [])

  const lastStatus = exchanges.at(-1)?.status
  useEffect(() => {
    if (!autoScroll || !stickToBottomRef.current) return
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [autoScroll, exchanges.length, lastStatus])

  return (
    <div ref={scrollRef} className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-6xl px-4 py-6">
        <div className="space-y-8">
          {exchanges.map((exchange) => (
            <ExchangeItem key={exchange.id} exchange={exchange} onRerun={onRerun} />
          ))}
        </div>
        <div ref={bottomRef} />
      </div>
    </div>
  )
}
